import React from "react";
import { useNavigate } from "react-router-dom";

// SOFTCAT 홈페이지 — claude.ai/design 산출물을 그대로 이식.
// 히어로(자동화 로그 터미널) · 마퀴 · 지표 · 하는 일 · 진행 방식 · 고객의 말 · CTA

const ARROW = (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
);

const CHANNELS = ["N사", "카페", "블로그", "SNS", "Y사", "쇼핑몰", "오픈마켓", "엑셀·리포트", "문자 발송"];

const LOGS = [
    "N사 검색 결과 1,240건 → 엑셀 저장",
    "카페 게시글 3건 예약 발행 완료",
    "재고 동기화: 오픈마켓 A↔B 214개 품목",
    "SNS DM 신규 문의 2건 자동 응답",
    "블로그 순위 기록: 키워드 38개",
    "거래명세 172건 장부 변환",
    "문자 발송 예약 등록: 08:00 · 340명",
    "Y사 댓글 1,905건 백업",
];

// 자동화 로그 터미널 — 1.6초마다 한 줄씩, 최대 10줄 유지
function useAutomationLog() {
    const [lines, setLines] = React.useState([]);
    const state = React.useRef({ i: 0, h: 9, m: 12, s: 3 });

    React.useEffect(() => {
        const tick = () => {
            const st = state.current;
            st.s += 7 + Math.floor(Math.random() * 40);
            if (st.s > 59) { st.s -= 60; st.m++; }
            if (st.m > 59) { st.m -= 60; st.h = (st.h + 1) % 24; }
            const t = [st.h, st.m, st.s].map((n) => String(n).padStart(2, "0")).join(":");
            const text = LOGS[st.i % LOGS.length];
            // id 는 여기서 확정한다. 업데이터 안에서 st.i 를 읽으면 안 된다 —
            // st 는 ref 라 살아 있는 값이고, React 는 업데이터를 나중에 몰아서
            // 실행하므로 아래 초기 8회 루프가 끝난 뒤 전부 같은 값을 보게 된다.
            // 그러면 key 가 겹쳐 React 가 지워야 할 행을 남기고 로그가 겹쳐 쌓인다.
            const id = ++st.i;
            setLines((prev) => [...prev, { id, t, text }].slice(-10));
        };
        for (let i = 0; i < 8; i++) tick();
        const timer = setInterval(tick, 1600);
        return () => clearInterval(timer);
    }, []);

    return lines;
}

export default function MainPage() {
    const navigate = useNavigate();
    const logLines = useAutomationLog();

    React.useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const go = (path) => {
        navigate(path);
        window.scrollTo(0, 0);
    };

    return (
        <>
            {/* ── 히어로 */}
            <section className="sch-hero">
                <div className="sch-wrap sch-hero__grid">
                    <div className="sch-hero__left">
                        <div>
                            <h1>사람은 생각을,<br />반복은 <em>기계</em>가.</h1>
                            <p className="sch-hero__sub">
                                소프트캣은 수집·정리·발행·발송 같은 반복 업무를 대신하는
                                자동화 소프트웨어를 만드는 개발사입니다.
                            </p>
                        </div>
                        <div className="sch-hero__foot">
                            <button className="sch-bigbtn" onClick={() => go("/custom-product-first")}>
                                무료 견적 받기{ARROW}
                            </button>
                            <span className="sch-ghostlink" onClick={() => go("/portfolio")}>
                                포트폴리오 보기 ↗
                            </span>
                            <span className="sch-ctanote">영업일 1~2일 내 회신 · 상담 무료</span>
                        </div>
                    </div>

                    <div className="sch-hero__right">
                        <div className="sch-term">
                            <div className="sch-term__bar">
                                <i /><i /><i />
                                <span>softcat — 오늘의 자동화 로그</span>
                            </div>
                            <div className="sch-term__log">
                                {logLines.map((l) => (
                                    <div key={l.id}>
                                        <span className="sch-t">[{l.t}]</span>
                                        {l.text} <span className="sch-ok">✓</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="sch-term__cap">
                            ↑ 지금 이 순간에도 고객사의 반복 업무가 자동으로 처리되고 있습니다
                        </div>
                    </div>
                </div>
            </section>

            {/* ── 마퀴 */}
            <div className="sch-marquee">
                <div className="sch-marquee__in">
                    {[0, 1].map((rep) =>
                        CHANNELS.map((c) => (
                            <React.Fragment key={`${rep}-${c}`}>
                                <span>{c}</span><b>✳</b>
                            </React.Fragment>
                        ))
                    )}
                </div>
            </div>

            {/* ── 지표 */}
            <div className="sch-statstrip sch-wrap" style={{ padding: 0 }}>
                <div className="sch-st"><div className="sch-v">120<em>+</em></div><div className="sch-k">누적 개발 프로젝트</div></div>
                <div className="sch-st"><div className="sch-v">30<em>+</em></div><div className="sch-k">자체 자동화 도구</div></div>
                <div className="sch-st"><div className="sch-v">4.8<em>/5</em></div><div className="sch-k">평균 고객 별점</div></div>
                <div className="sch-st"><div className="sch-v">48<em>h</em></div><div className="sch-k">상담 회신 시간</div></div>
            </div>

            {/* ── (01) 하는 일 */}
            <section className="sch-sec">
                <div className="sch-wrap sch-sec__grid">
                    <div className="sch-sec__label">
                        <div className="sch-no">(01)</div>
                        <h2>하는 일</h2>
                        <p>기성 도구 판매부터 완전 맞춤 개발까지, 자동화의 전 과정을 다룹니다.</p>
                    </div>
                    <div>
                        <div className="sch-svcrow">
                            <div className="sch-i">A.</div>
                            <h3>기성 자동화 도구</h3>
                            <p>검증된 30여 종의 자동화 프로그램을 바로 도입합니다. 설치 가이드와 동영상 매뉴얼이 함께 갑니다.</p>
                        </div>
                        <div className="sch-svcrow">
                            <div className="sch-i">B.</div>
                            <h3>맞춤 개발</h3>
                            <p>업무에 딱 맞는 도구를 요구사항 명세부터 납품·교육까지 책임지고 개발합니다. 견적 상담은 무료입니다.</p>
                        </div>
                        <div className="sch-svcrow">
                            <div className="sch-i">C.</div>
                            <h3>유지보수 · A/S</h3>
                            <p>플랫폼이 바뀌어 도구가 멈추면 신속히 대응합니다. 정기 업데이트와 원격 지원을 제공합니다.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── (03) 진행 방식 */}
            <section className="sch-sec">
                <div className="sch-wrap sch-sec__grid">
                    <div className="sch-sec__label">
                        <div className="sch-no">(02)</div>
                        <h2>진행 방식</h2>
                    </div>
                    <div className="sch-steps">
                        <div className="sch-s">
                            <div className="sch-n">STEP 01</div><h3>요구사항 상담</h3>
                            <p>문의 폼·카카오톡으로 필요한 업무를 알려주세요. 영업일 1~2일 내 회신드려요.</p>
                        </div>
                        <div className="sch-s">
                            <div className="sch-n">STEP 02</div><h3>견적·일정 확정</h3>
                            <p>기능 명세서를 함께 정리하고 견적과 일정을 확정합니다.</p>
                        </div>
                        <div className="sch-s">
                            <div className="sch-n">STEP 03</div><h3>개발·중간 시연</h3>
                            <p>중간 시연으로 방향을 맞추며 개발합니다.</p>
                        </div>
                        <div className="sch-s">
                            <div className="sch-n">STEP 04</div><h3>납품·A/S</h3>
                            <p>설치 가이드·매뉴얼과 함께 납품하고 이후를 지원합니다.</p>
                        </div>

                        <div className="sch-sectioncta">
                            <span>1단계는 간단한 상담 신청입니다.</span>
                            <button className="sc-btn" onClick={() => go("/custom-product-first")}>
                                무료 견적 받기
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── (04) 고객의 말 */}
            <section className="sch-sec">
                <div className="sch-wrap sch-sec__grid">
                    <div className="sch-sec__label">
                        <div className="sch-no">(03)</div>
                        <h2>고객의 말</h2>
                    </div>
                    <div className="sch-qs">
                        <div className="sch-q">
                            <div className="sch-mark">“</div>
                            <p>다양한 기능이 포함되어 있어서 정말 만족합니다. 설치도 간편하고 빠르네요.</p>
                            <div className="sch-who">소프트웨어 A 구매 고객 — ★ 4.7</div>
                        </div>
                        <div className="sch-q">
                            <div className="sch-mark">“</div>
                            <p>지금까지 사용한 자동화 소프트웨어 중 최고. 속도 빠르고 만족스럽습니다.</p>
                            <div className="sch-who">자동화 솔루션 사용자 — ★ 5.0</div>
                        </div>
                        <div className="sch-q">
                            <div className="sch-mark">“</div>
                            <p>업무 시간이 하루 2시간 이상 줄었어요. 요구사항 반영도 꼼꼼했습니다.</p>
                            <div className="sch-who">맞춤 개발 의뢰 고객 — ★ 4.5</div>
                        </div>

                        <div className="sch-sectioncta">
                            <span>같은 결과를 원하신다면</span>
                            <button className="sc-btn" onClick={() => go("/custom-product-first")}>
                                무료 견적 받기
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── CTA */}
            <section className="sch-ctax">
                <div className="sch-wrap">
                    <h2>만들고 싶은<br /><em>자동화</em>가 있나요?</h2>
                    <button
                        className="sch-bigbtn"
                        style={{ background: "#fff", color: "var(--primary-dark)" }}
                        onClick={() => go("/custom-product-first")}
                    >
                        무료 견적 받기{ARROW}
                    </button>
                </div>
            </section>
        </>
    );
}
